// B122 Phase 2 SSOT: per-entity tax slice. SMSF/Company branches must
// extend this signature, not duplicate.
//
// Pure aggregator over existing helpers (allocateTrustNetIncome,
// applyEntityLossCarryForward, resolveBeneficiaryDistribution,
// cgtDiscountRate, landTaxAggregation, prorateFactor). Reads existing
// propertyTaxData rows + structures + cgtEvents; returns a shaped slice
// for a single entity.
//
// Multi-path consistency invariant (R8): the slice's
// `carryForward` triple ≡ direct applyEntityLossCarryForward call;
// `perBeneficiary[i].resolved` ≡ direct resolveBeneficiaryDistribution call.
// Tested in entityTaxData.test.ts + multi-path-consistency.test.ts.

import type { Structure, Beneficiary } from "../types/entities";
import type { PropertyRecord } from "../types/portfolio";
import { applyEntityLossCarryForward, type LossCarryForwardResult } from "./trustLossCarryForward";
import { resolveBeneficiaryDistribution, type ResolvedDistribution } from "./beneficiaryResolution";
import { landTaxAggregation, type LandTaxInputRow, type LandTaxGroupRow } from "./landTaxAggregation";
import { annualLoanInterest } from "./calculations";
import { prorateFactor } from "./saleProrate";
// B122-P3 v2 (Day 81) — circular function-import; both modules use the
// imported values inside function bodies, so ESM evaluation resolves
// cleanly at runtime. Same pattern as calculations.ts ↔ saleProrate.ts.
import { annualIncome, annualExpenses, annualDepreciation } from "./buildPortfolioContext";

/**
 * Minimal shape required from each propertyTaxData row. Accepts the full
 * TaxPage propertyTaxData[] entries via structural typing (TaxPage's
 * inferred row type satisfies this interface).
 */
export interface PropertyTaxRow {
  p: PropertyRecord;
  rent: number;
  opex: number;
  interest: number;
  depr: { total: number; div43: number; div40: number };
  borrowingCosts: number;
  totalDeductions: number;
  netIncomeLoss: number;
  entityType: string;
  entityName: string;
  isPersonal: boolean;
  ownershipFactor: number;
  hasActuals: boolean;
}

/**
 * CGT event row shape consumed by entityTaxData. Mirrors the TaxPage local
 * CgtEvent interface; structurally compatible with the actual `cgt_events`
 * row from Supabase. `property_id` is in the underlying row but optional
 * here to match TaxPage's local type narrowing.
 */
export interface CGTEventRow {
  id: string;
  property_address: string;
  entity_name: string | null;
  entity_type: string | null;
  purchase_date: string | null;
  sale_date: string;
  purchase_price: number | null;
  sale_price: number;
  cost_base: number | null;
  sale_costs: number | null;
  gross_profit: number | null;
  held_over_12_months: boolean;
  cgt_discount_applied: boolean;
  taxable_gain: number | null;
  notes: string | null;
}

export interface EntityTaxData {
  structureId: string;
  entityType: Structure["entityType"];
  /** Filtered subset of propertyTaxData where p.structureId === structureId. */
  properties: PropertyTaxRow[];
  /** Sum of net rental (netIncomeLoss) across this entity's properties. */
  grossNetIncome: number;
  /** Result of applyEntityLossCarryForward(structure, fy, grossNetIncome). */
  carryForward: LossCarryForwardResult;
  /** Convenience accessor — equals carryForward.adjustedNetIncome. */
  adjustedNetIncome: number;
  /** Per-beneficiary distribution resolution against adjustedNetIncome. */
  perBeneficiary: Array<{ beneficiary: Beneficiary; resolved: ResolvedDistribution }>;
  /** CGT events filtered to this entity (matched by entity_name). */
  cgtEvents: CGTEventRow[];
  /** Land-tax projection for the trust panel: estimatedTax per state. */
  landTaxByState: Map<string, number>;
  /** Sum across landTaxByState. */
  landTaxTotal: number;
  /** Underlying full-shape land-tax rows (rich, with surcharge fields) for this entity. */
  landTaxGroups: LandTaxGroupRow[];
}

export interface EntityTaxDataOpts {
  structures: Structure[];
  /** Full propertyTaxData array (already lens/proRata-applied per TaxPage build). */
  propertyTaxData: PropertyTaxRow[];
  cgtEvents: CGTEventRow[];
  fy: string;
}

/**
 * B122 Phase 2 SSOT: per-entity tax slice.
 *
 * Filtering rules:
 * - properties: strict equality on `p.structureId === structureId` (no
 *   truthy fallback that could leak null-structureId rows into a trust).
 * - cgtEvents: matches when `event.entity_name === structure.displayName`.
 *   Same brittleness as today's CGT panel rendering (entity_name is a
 *   denormalised copy at insert-time; rename of structure.displayName
 *   leaves old events orphaned). Not introducing a new bug; not solving
 *   an old one. Future: add `cgt_events.structure_id` foreign key.
 *
 * Multi-path-consistency: see entityTaxData.test.ts (R4.6) and
 * multi-path-consistency.test.ts B122 P2 block (R8).
 */
export function entityTaxData(
  structureId: string,
  opts: EntityTaxDataOpts,
): EntityTaxData {
  const structure = opts.structures.find((s) => s.id === structureId);
  if (!structure) {
    throw new Error(
      `[B122-P2 entityTaxData] structure not found: ${structureId}`,
    );
  }

  // Strict filter — no truthy-fallback leak.
  const properties = opts.propertyTaxData.filter(
    (d) => d.p.structureId === structureId,
  );

  // Sum of per-row netIncomeLoss = sum of net rental for this entity.
  const grossNetIncome = properties.reduce((s, d) => s + d.netIncomeLoss, 0);

  // B125 — apply trust loss carry-forward against grossNetIncome.
  const carryForward = applyEntityLossCarryForward(
    structure,
    opts.fy,
    grossNetIncome,
  );
  const adjustedNetIncome = carryForward.adjustedNetIncome;

  // B121 — per-beneficiary distribution resolution against adjustedNetIncome.
  const beneficiaries = structure.beneficiaries ?? [];
  const perBeneficiary = beneficiaries.map((b) => ({
    beneficiary: b,
    resolved: resolveBeneficiaryDistribution(
      b,
      opts.fy,
      adjustedNetIncome,
      beneficiaries,
      structure.entityType,
    ),
  }));

  // CGT events filtered by denormalised entity_name (see JSDoc note above).
  const cgtEvents = opts.cgtEvents.filter(
    (e) => e.entity_name === structure.displayName,
  );

  // Land tax projection: full per-entity rows from the SSOT, filtered to
  // this entity's structureId, then projected to byState/total.
  const allLandTaxGroups = landTaxAggregation(
    opts.propertyTaxData as LandTaxInputRow[],
  );
  const landTaxGroups = allLandTaxGroups.filter(
    (g) => g.structureId === structureId,
  );
  const landTaxByState = new Map<string, number>();
  for (const g of landTaxGroups) {
    landTaxByState.set(g.state, (landTaxByState.get(g.state) ?? 0) + g.estimatedTax);
  }
  const landTaxTotal = landTaxGroups.reduce((s, g) => s + g.estimatedTax, 0);

  return {
    structureId,
    entityType: structure.entityType,
    properties,
    grossNetIncome,
    carryForward,
    adjustedNetIncome,
    perBeneficiary,
    cgtEvents,
    landTaxByState,
    landTaxTotal,
    landTaxGroups,
  };
}

// ─── B122 P3 — Personal Item 13 (received-side trust distributions) ─────────

/**
 * B122 P3 SSOT — per-Personal-entity received-side trust distribution view.
 *
 * Aggregates across all in-app non-SMSF structures where the given
 * householdMemberId is linked as a beneficiary (B103 Phase 1 link).
 * Pure aggregator over B121 resolveBeneficiaryDistribution.
 *
 * Excludes:
 * - SMSF structures (statutory entitlement, not s.97 distribution)
 * - Bucket-company beneficiaries (corporate, not Personal Item 13)
 * - Beneficiaries without householdMemberId (manual-add, not profile-linked)
 * - Zero-amount allocations (loss-quarantined trusts)
 *
 * FWFT (out-of-app trusts) NOT covered — separate wave.
 *
 * Multi-path-consistency invariant (R8): for any in-app non-SMSF structure,
 * sum of personalTrustDistributions(hmId).rows.filter(r => r.trustId === id).amount
 * across all hmIds === sum of resolveBeneficiaryDistribution(b, fy, ...).amount
 * for matching beneficiaries.
 */
export interface PersonalDistributionRow {
  trustId: string;
  trustName: string;
  percent: number;
  amount: number;
  source: ResolvedDistribution["source"];
  actualDistributed?: number;
}

export interface PersonalTrustDistributionsResult {
  rows: PersonalDistributionRow[];
  total: number;
}

export interface PersonalTrustDistributionsOpts {
  structures: Structure[];
  /** Raw, un-lensed properties (single source of truth — no lens scoping). */
  properties: PropertyRecord[];
  fy: string;
}

/**
 * Build a trustee-scope (un-lensed) PropertyTaxRow for one property.
 * Mirrors the buildPortfolioContext.ts row shape, with ownershipFactor = 1
 * (trust owns the property; lens-member share is irrelevant for trustee
 * net income calculation).
 *
 * B122-P3 v2 (Day 81): replaces the c3b2795 shape-adapter approach.
 * Lens-scoping at TaxPage propertyTaxData would zero-out trust property
 * contributions when Personal lens is active, leaving Item13Panel empty
 * (Box 7 finding). Trustee-scope build is the only correct path.
 */
function buildTrusteeRow(p: PropertyRecord, fy: string): PropertyTaxRow {
  const proRata = prorateFactor(p, fy);
  const rent = annualIncome(p) * proRata;
  const opex = annualExpenses(p) * proRata;
  const interest = annualLoanInterest(p) * proRata;
  const deprRaw = annualDepreciation(p, fy);
  const totalDeductions = opex + interest + deprRaw.total;
  return {
    p,
    rent,
    opex,
    interest,
    depr: deprRaw,
    borrowingCosts: 0,
    totalDeductions,
    netIncomeLoss: rent - totalDeductions,
    entityType: "Trust",
    entityName: "",
    isPersonal: false,
    ownershipFactor: 1,
    hasActuals: false,
  };
}

export function personalTrustDistributions(
  householdMemberId: string,
  opts: PersonalTrustDistributionsOpts,
): PersonalTrustDistributionsResult {
  const rows: PersonalDistributionRow[] = [];

  for (const structure of opts.structures) {
    if (structure.entityType === "SMSF") continue;
    if (structure.entityType !== "Trust" && structure.entityType !== "Company / Trust") continue;

    const beneficiaries = structure.beneficiaries ?? [];
    const matched = beneficiaries.find(
      (b) => b.householdMemberId === householdMemberId && b.isBucketCompany !== true,
    );
    if (!matched) continue;

    // B122-P3 v2: build trustee-scope (un-lensed) propertyTaxData per trust.
    // Filters opts.properties by structureId; ownershipFactor = 1.
    const trustProps = opts.properties.filter((p) => p.structureId === structure.id);
    const trusteeRows = trustProps.map((p) => buildTrusteeRow(p, opts.fy));

    // Compute adjustedNet via entityTaxData (carries B125 carry-forward).
    const slice = entityTaxData(structure.id, {
      structures: opts.structures,
      propertyTaxData: trusteeRows,
      cgtEvents: [],
      fy: opts.fy,
    });

    const resolved = resolveBeneficiaryDistribution(
      matched,
      opts.fy,
      slice.adjustedNetIncome,
      beneficiaries,
      structure.entityType,
    );

    if (resolved.amount <= 0) continue;

    rows.push({
      trustId: structure.id,
      trustName: structure.displayName ?? structure.entityType,
      percent: resolved.percent,
      amount: resolved.amount,
      source: resolved.source,
      actualDistributed: resolved.actualDistributed,
    });
  }

  const total = rows.reduce((s, r) => s + r.amount, 0);
  return { rows, total };
}

// ─── B122 P4 — SMSF Fund Summary (Stearman) ─────────────────────────────────

/**
 * Hardcoded SMSF concessional tax rate per ITAA 1997 s.295-385. Statutory —
 * not user-overridable. Any future "override" mechanic on this constant is
 * a defect class (audit R9). Contributions tax (also 15%) feeds the same
 * §D label T1 line but is a separate computation; out of P4 scope.
 */
export const SMSF_CONCESSIONAL_RATE = 0.15;

export interface SMSFMember {
  /** Beneficiary id from structures.beneficiaries[]. */
  beneficiaryId: string;
  /** Display name. */
  name: string;
  /** Linked household-member id (primary / hm-XYZ). May be undefined for
   *  manual-add members not linked to a HouseholdMember. */
  householdMemberId?: string;
  /** Statutory entitlement percentage (read directly from
   *  beneficiaries[].distributionPercent — no resolveBeneficiaryDistribution
   *  call). For SMSF this is member balance share, not s.97 discretion. */
  distributionPercent: number;
  /** Always 'smsf-statutory' per ITAA 1997 s.295-385. No fy-entry /
   *  carry-forward / legacy-percent variants — direct read. */
  source: "smsf-statutory";
}

export interface SMSFFundSummary {
  structureId: string;
  displayName: string;
  fy: string;
  // Assessable income (§B labels in SMSF Annual Return)
  rent: number;                  // §B label B (gross rent)
  interestIncome: number;        // §B label C — V1: always 0, gap flagged
  employerContributions: number; // §B label R1 — V1: always 0, gap flagged
  assessableIncome: number;      // sum of above
  // Deductions (§C labels)
  loanInterest: number;          // §C label A1 (LRBA interest)
  propertyOpex: number;          // §C label I1 (rates / insurance / PM / strata)
  depreciation: number;          // Div 43 + Div 40 (per Wave 2B/2C)
  totalDeductions: number;
  // Tax (§D)
  taxableIncome: number;         // §D label A (= assessable − deductions)
  taxRate: number;               // hardcoded 0.15 (s.295-385)
  tax: number;                   // §D label T1 (= taxableIncome × 0.15; never < 0)
  // Member shape (statutory, not s.97 discretion)
  members: SMSFMember[];
  // Land tax — reuses landTaxAggregation SSOT filtered to this SMSF.
  landTaxByState: Map<string, number>;
  landTaxTotal: number;
  landTaxGroups: LandTaxGroupRow[];
  // CGT events filtered to this SMSF (via entity_name match per entityTaxData).
  cgtEvents: CGTEventRow[];
  // Known-data-gap flags for UI annotation (R4 excluded scope).
  gaps: {
    contributionsNotTracked: boolean;
    interestIncomeNotTracked: boolean;
    memberBalanceNotTracked: boolean;
  };
}

export interface SMSFFundSummaryOpts {
  /** Raw, un-lensed properties (single source of truth — no lens scoping per L71). */
  properties: PropertyRecord[];
  /** Structures array — used to locate the SMSF by id + filter properties. */
  structures: Structure[];
  /** CGT events (typically empty for SMSF V1; reused if present). */
  cgtEvents?: CGTEventRow[];
  /** Canonical "YYYY-YY" current-FY label (normalised). */
  fy: string;
}

/**
 * B122 Phase 4 SSOT — SMSF Fund Summary.
 *
 * Returns null if the structureId does not resolve to an entity_type === 'SMSF'
 * structure (strict uppercase equality per audit R3).
 *
 * Invariants (R3):
 * - Hardcoded 15% rate (SMSF_CONCESSIONAL_RATE) — never delegates to personal
 *   marginal calc, never reads tax-rules-{FY}.ts.
 * - ownershipFactor = 1 (trustee scope, L71). Raw-input signature — never
 *   consumes pre-derived TaxPage propertyTaxData (which is lens-scoped).
 * - Never calls resolveBeneficiaryDistribution — member entitlement is direct
 *   read from structures.beneficiaries[] (statutory per s.295-385).
 * - Never reads property.currentValue / valueHistory — unrealised market-value
 *   movement is non-assessable per s.295-160 (R11 #8).
 * - tax = max(0, taxableIncome * 0.15) — no negative tax (a fund loss carries
 *   forward as a CGT loss; the §D label T1 tax payable is floored at 0).
 */
export function smsfFundSummary(
  structureId: string,
  opts: SMSFFundSummaryOpts,
): SMSFFundSummary | null {
  const structure = opts.structures.find((s) => s.id === structureId);
  if (!structure) return null;
  // Strict uppercase equality — case-insensitive filter is a defect (R3).
  if (structure.entityType !== "SMSF") return null;

  // Filter to this SMSF's properties (strict structureId equality).
  const smsfProps = opts.properties.filter((p) => p.structureId === structureId);

  // Build trustee-scope rows internally (raw-input pattern per L71).
  // Mirrors P3 buildTrusteeRow shape; ownershipFactor = 1 implicit (we read
  // raw annualIncome/annualExpenses, no lens narrowing). Never reads
  // currentValue — only flow-based fields (R11 #8).
  let rent = 0;
  let propertyOpex = 0;
  let loanInterest = 0;
  let depreciation = 0;
  for (const p of smsfProps) {
    const proRata = prorateFactor(p, opts.fy);
    rent += annualIncome(p) * proRata;
    propertyOpex += annualExpenses(p) * proRata;
    loanInterest += annualLoanInterest(p) * proRata;
    depreciation += annualDepreciation(p, opts.fy).total;
  }

  // V1 gaps — known not modelled (audit R4 / R5).
  const interestIncome = 0;
  const employerContributions = 0;

  const assessableIncome = rent + interestIncome + employerContributions;
  const totalDeductions = loanInterest + propertyOpex + depreciation;
  const taxableIncome = assessableIncome - totalDeductions;
  // §D label T1: tax payable on positive taxable income only. A fund loss
  // carries forward but the line-item tax is 0 — never negative.
  const tax = Math.max(0, taxableIncome) * SMSF_CONCESSIONAL_RATE;

  // Member shape — direct read, no resolver call (R11 #2).
  const beneficiaries = structure.beneficiaries ?? [];
  const members: SMSFMember[] = beneficiaries.map((b) => ({
    beneficiaryId: b.id,
    name: b.name,
    householdMemberId: b.householdMemberId,
    distributionPercent: b.distributionPercent ?? 0,
    source: "smsf-statutory" as const,
  }));

  // Land tax — reuse the existing SSOT. Build trustee-scope rows for the
  // aggregator (only the structureId + state + ULV fields are read by
  // landTaxAggregation; full row shape inferred from PropertyTaxRow).
  const landTaxRows: LandTaxInputRow[] = smsfProps.map((p) => {
    const proRata = prorateFactor(p, opts.fy);
    const propRent = annualIncome(p) * proRata;
    const propOpex = annualExpenses(p) * proRata;
    const propInterest = annualLoanInterest(p) * proRata;
    const propDepr = annualDepreciation(p, opts.fy);
    return {
      p,
      rent: propRent,
      opex: propOpex,
      interest: propInterest,
      depr: propDepr,
      borrowingCosts: 0,
      totalDeductions: propOpex + propInterest + propDepr.total,
      netIncomeLoss: propRent - (propOpex + propInterest + propDepr.total),
      entityType: "SMSF",
      entityName: structure.displayName,
      isPersonal: false,
      ownershipFactor: 1,
      hasActuals: false,
    } as LandTaxInputRow;
  });
  const allLandTaxGroups = landTaxAggregation(landTaxRows);
  const landTaxGroups = allLandTaxGroups.filter((g) => g.structureId === structureId);
  const landTaxByState = new Map<string, number>();
  for (const g of landTaxGroups) {
    landTaxByState.set(g.state, (landTaxByState.get(g.state) ?? 0) + g.estimatedTax);
  }
  const landTaxTotal = landTaxGroups.reduce((s, g) => s + g.estimatedTax, 0);

  // CGT events — denormalised entity_name match (same brittleness as
  // entityTaxData; future B-item to add structure_id FK).
  const cgtEvents = (opts.cgtEvents ?? []).filter(
    (e) => e.entity_name === structure.displayName,
  );

  return {
    structureId,
    displayName: structure.displayName,
    fy: opts.fy,
    rent,
    interestIncome,
    employerContributions,
    assessableIncome,
    loanInterest,
    propertyOpex,
    depreciation,
    totalDeductions,
    taxableIncome,
    taxRate: SMSF_CONCESSIONAL_RATE,
    tax,
    members,
    landTaxByState,
    landTaxTotal,
    landTaxGroups,
    cgtEvents,
    gaps: {
      contributionsNotTracked: true,
      interestIncomeNotTracked: true,
      memberBalanceNotTracked: true,
    },
  };
}
